import type { Brand } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';

import { BRAND } from 'src/shared/constant';

import translations from './.vocab';

type DataType = Record<string, Array<string | false>>;

export const useSubjects = (brand: Brand) => {
  const { t } = useTranslations(translations);

  const signIn = {
    [BRAND.seek]: t('Sign in / Password'),
    [BRAND.jobsdb]: t('Login / Password'),
    [BRAND.jobstreet]: t('Login / Password'),
  };

  const siteName = {
    [BRAND.seek]: 'SEEK',
    [BRAND.jobsdb]: 'Jobsdb',
    [BRAND.jobstreet]: 'Jobstreet',
  };

  const jobMail = {
    [BRAND.seek]: t('JobMail'),
    [BRAND.jobsdb]: t('Job recommendation emails'),
    [BRAND.jobstreet]: t('Job recommendation emails'),
  };

  const subjectList: DataType = {
    [signIn[brand]]: [
      t('Forgot my username or password'),
      t('Sign in error'),
      t('My account has been blocked'),
      t('I didn’t receive my password reset e-mail'),
      t('Other'),
    ],
    [t('My Account')]: [
      t('Update account details'),
      t('Update my e-mail address'),
      t('Registration'),
      t('Applied Jobs'),
      t('Saved jobs'),
      t('Delete account'),
      t('Other'),
    ],
    [t('Job Search')]: [
      brand === BRAND.seek && t('How does SEEK’s search engine work?'),
      t('How to search for a job'),
      t('Job search issues/error'),
      brand === BRAND.seek && t('Location search'),
      brand === BRAND.seek && t('Salary'),
      t('Other'),
    ],
    [t('Applying for a job')]: [
      t('Application error'),
      t('How to apply'),
      brand === BRAND.seek && t('Certsy'),
      t('Privacy & data protection'),
      brand === BRAND.seek && t('International application / Visa queries'),
      t('Cover letter'),
      t('Withdrawal of application'),
      t('Follow up application'),
      t('Other'),
    ],
    [t('{brandName} Profile', { brandName: siteName[brand] })]: [
      t('What is a {brandName} Profile', { brandName: siteName[brand] }),
      t('Create or Upload a resumé'),
      t('Edit or Delete a resumé'),
      t('Update my profile'),
      t('My profile visibility'),
      t('Delete my profile'),
      t('Profile sent with application'),
      t('Other'),
    ],
    [t('Report a suspicious ad')]: [
      t('Report a suspicious ad'),
      t('Report a suspicious hirer'),
    ],
    [t('Email Issues')]: [
      t('I’ve stopped receiving e-mails'),
      t('I need to update my e-mail address'),
      t('Unsubscribe from emails'),
      jobMail[brand],
      brand === BRAND.seek && t('Weekly Round-up'),
      t('Other'),
    ],
    [t('Other')]: [
      t('General feedback'),
      t('Product feedback'),
      t('Company Reviews'),
    ],
  };

  /* Remove any nullish/falsey values when reasons are removed due to locale */
  const filterList = () => {
    const newList: Record<string, string[]> = {};

    // eslint-disable-next-line guard-for-in
    for (const subject in subjectList) {
      newList[subject] = subjectList[subject].filter(Boolean) as string[];
    }

    return newList;
  };

  return { subjects: filterList() };
};
